import { createFileRoute } from "@tanstack/react-router";
import { useMemo, useState } from "react";
import {
  Box,
  Button,
  ColorSwatch,
  EmptyState,
  ExpandableSearch,
  Icon,
  Menu,
  MenuItem,
  MenuPopup,
  MenuSeparator,
  MenuTrigger,
  ScrollArea,
  Skeleton,
  Switch,
  Text,
  Tooltip,
  TooltipPopup,
  TooltipTrigger,
} from "@salestrak/ui";
import { DEFAULT_META_LABEL } from "#app/components/entity";
import { PageHeader, PageHeaderSubtitle, PageHeaderTitle, SETTINGS_CONTENT_MAX_WIDTH, SettingsPageGutter } from "#app/components/page-header";
import { useIssueSourceSettings } from "#app/lib/hooks/use-issue-source-settings";
import {
  useIssueSources,
  useIssueSourceMutations,
  type IssueSource,
} from "#app/hooks/settings/use-issue-source-mutations";
import { SettingsGroup, SettingsGroupHeader, SettingsGroupRow } from "#app/components/settings/settings-group";
import { LoadErrorState } from "#app/components/settings/load-error-state";
import { IssueSourceFormDialog } from "#app/components/settings/issue-source-form-dialog";
import { TrashConfirmDialog } from "#app/components/settings/trash-confirm-dialog";
import { DisabledReason, NO_PERMISSION } from "#app/components/menus/disabled-reason";
import { withInternal } from "#app/components/permissions/require-permission";
import { usePermissionVerdict } from "#app/lib/hooks/use-permission";
import { useAppData } from "#app/lib/data";
import { SettingsGroupRowsSkeleton, SettingsPageSkeleton } from "#app/components/settings/settings-page-skeleton";

function SourcesList() {
  // The page reads through the same verdict the picker resolves: `yes` holds
  // the whole pool; `if_created` creates and manages (rename, enable/disable)
  // only its own rows — the policy, default and delete stay workspace-wide;
  // `no` reads.
  const verdict = usePermissionVerdict("issue_sources.manage");
  const { currentUser } = useAppData();
  const canManageAll = verdict === "yes";
  const canCreate = verdict !== "no";
  const managesRow = (source: IssueSource): boolean =>
    canManageAll ||
    (verdict === "if_created" &&
      currentUser != null &&
      source.createdBy === currentUser.userId);
  const { settings, updateSetting } = useIssueSourceSettings();
  const { issueSources, total, isLoading, error, refetch } = useIssueSources();
  const {
    createIssueSource,
    updateIssueSource,
    toggleIssueSource,
    deleteIssueSource,
  } = useIssueSourceMutations();

  const policy = settings.projectSourcePolicy;
  // Switched off, a Project carries no Source, so it has nothing to default:
  // the marker and the menu rows stay away until the property is on again (a
  // stored default survives the flip unshown, like the values do).
  const defaultId = policy === "off" ? null : (settings.projectDefaultSourceId ?? null);

  const [createOpen, setCreateOpen] = useState(false);
  const [editingSource, setEditingSource] = useState<IssueSource | null>(null);
  const [deletingSource, setDeletingSource] = useState<IssueSource | null>(null);
  const [openMenuId, setOpenMenuId] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState("");

  const filteredSources = useMemo(
    () =>
      issueSources.filter((source) =>
        source.name.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [issueSources, searchQuery],
  );

  // The header and card chrome are static, so they render for real while the
  // list reserves skeleton rows in place.
  const listLoading = isLoading && issueSources.length === 0;

  if (error && issueSources.length === 0) {
    return (
      <LoadErrorState
        noun="sources"
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <>
      {/* Whether a Project carries the property at all. Off hides the Source
          row everywhere and keeps stored values unshown; whether a create must
          set one is each Type's call (Settings → Types → Properties).
          Workspace-wide, so an `if_created` grant reads it like everyone
          else — only the full key edits. */}
      <SettingsGroup>
        <SettingsGroupRow
          label="Enable Source on Projects"
          subtitle="Adds a Source property to every Project"
        >
          {canManageAll ? (
            // The trigger wraps the switch, the way DisabledReason does it:
            // the Switch primitive renders its own root and does not carry a
            // TooltipTrigger's merged hover handlers through. The tooltip
            // names the action the press takes, by state.
            <Tooltip>
              <TooltipTrigger render={<Box direction="row" align="center" />}>
                <Switch
                  aria-label="Enable Source on Projects"
                  checked={policy !== "off"}
                  onCheckedChange={(on) =>
                    updateSetting({ projectSourcePolicy: on ? "optional" : "off" })
                  }
                />
              </TooltipTrigger>
              <TooltipPopup>{policy === "off" ? "Enable" : "Disable"}</TooltipPopup>
            </Tooltip>
          ) : (
            <DisabledReason reason={NO_PERMISSION}>
              <Switch
                aria-label="Enable Source on Projects"
                checked={policy !== "off"}
                disabled
              />
            </DisabledReason>
          )}
        </SettingsGroupRow>
      </SettingsGroup>

      <SettingsGroup>
        {/* The page title already names the entity, so the header row carries
            the count, the collapsed search and the create action. The search
            is the list's own tool — it sits with the list's actions, left of
            the plus, and an empty list has nothing to search. */}
        <SettingsGroupHeader>
          {listLoading ? (
            <Skeleton delayed height={12} width={48} />
          ) : (
            <Text size="sm" weight="medium">
              {total} {total === 1 ? "Source" : "Sources"}
            </Text>
          )}
          <Box direction="row" align="center" gap="xs">
            {issueSources.length > 0 ? (
              <ExpandableSearch
                value={searchQuery}
                onValueChange={setSearchQuery}
                size="xs"
                tooltip="Search sources"
              />
            ) : null}
            {canCreate ? (
              <Button
                aria-label="Create source"
                variant="ghost"
                size="icon-xs"
                onClick={() => setCreateOpen(true)}
              >
                <Icon name="plus" size="md" />
              </Button>
            ) : null}
          </Box>
        </SettingsGroupHeader>

        {listLoading ? (
          <SettingsGroupRowsSkeleton rows={3} />
        ) : filteredSources.length === 0 ? (
          <Box paddingY="md">
            <EmptyState
              size="xs"
              title={searchQuery ? "No matching sources" : "No sources yet"}
            />
          </Box>
        ) : (
          filteredSources.map((source) => {
            const isDefault = !source.archived && source.issueSourceId === defaultId;
            return (
              <SettingsGroupRow
                key={source.issueSourceId}
                label={source.name}
                // The meta line: state, then this tab's default designation,
                // on the app's " · " meta grammar (the Types lists' word).
                subtitle={[source.archived ? "Disabled" : "Enabled", isDefault ? DEFAULT_META_LABEL : null]
                  .filter(Boolean)
                  .join(" · ")}
                leading={
                  <ColorSwatch
                    color={source.archived ? "grey" : "green"}
                    size="xs"
                    rounded="full"
                  />
                }
                actionsVisible={openMenuId === source.issueSourceId}
              >
                {managesRow(source) ? (
                  <Menu onOpenChange={(open) => setOpenMenuId(open ? source.issueSourceId : null)}>
                    <MenuTrigger
                      render={
                        <Button
                          aria-label={`Actions for ${source.name}`}
                          variant="ghost"
                          size="icon-xs"
                        />
                      }
                    >
                      <Icon name="ellipsis" size="md" />
                    </MenuTrigger>
                    <MenuPopup align="end" className="min-w-45">
                      {/* The default is the Source a new Project starts with,
                          and a disabled Source is never offered — so the
                          default moves or clears first (the server refuses
                          the same). */}
                      {isDefault ? (
                        <MenuItem
                          disabled
                          disabledReason="This is the default Source. Set another default or clear it first."
                        >
                          <Icon name="eye-closed" size="md" />
                          Disable source
                        </MenuItem>
                      ) : (
                        <MenuItem onClick={() => toggleIssueSource(source)}>
                          <Icon name="eye-closed" size="md" />
                          {source.archived ? "Enable source" : "Disable source"}
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => setEditingSource(source)}>
                        <Icon name="pen-line" size="md" />
                        Edit
                      </MenuItem>
                      {/* The default and delete are workspace-wide choices —
                          past `if_created`'s own-rows reach (the server
                          refuses both), so the rows only exist on the full
                          key. */}
                      {canManageAll && !source.archived && policy !== "off" && !isDefault ? (
                        <MenuItem onClick={() => updateSetting({ projectDefaultSourceId: source.issueSourceId })}>
                          <Icon name="check" size="md" />
                          Set as default
                        </MenuItem>
                      ) : null}
                      {canManageAll && isDefault ? (
                        <MenuItem onClick={() => updateSetting({ projectDefaultSourceId: null })}>
                          <Icon name="x-circle" size="md" />
                          Clear as default
                        </MenuItem>
                      ) : null}
                      {canManageAll ? (
                        <>
                          <MenuSeparator />
                          <MenuItem onClick={() => setDeletingSource(source)}>
                            <Icon name="trash" size="md" />
                            Delete...
                          </MenuItem>
                        </>
                      ) : null}
                    </MenuPopup>
                  </Menu>
                ) : null}
              </SettingsGroupRow>
            );
          })
        )}
      </SettingsGroup>

      <IssueSourceFormDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onSubmit={async (name) => {
          // A refusal (a taken name) is toasted by the hook; rejecting keeps
          // the dialog open on the typed name.
          if (!(await createIssueSource(name))) throw new Error("not created");
        }}
      />

      <IssueSourceFormDialog
        open={!!editingSource}
        onOpenChange={(open) => {
          if (!open) setEditingSource(null);
        }}
        initialName={editingSource?.name}
        onSubmit={async (name) => {
          if (editingSource && !(await updateIssueSource(editingSource.id, name))) {
            throw new Error("not updated");
          }
        }}
      />

      <TrashConfirmDialog
        open={!!deletingSource}
        onOpenChange={(open) => {
          if (!open) setDeletingSource(null);
        }}
        title={`Delete "${deletingSource?.name ?? ""}"?`}
        description="Projects lose this Source. This cannot be undone."
        confirmLabel="Delete"
        onConfirm={async () => {
          if (deletingSource) {
            await deleteIssueSource(deletingSource);
          }
        }}
      />
    </>
  );
}

function IssueSourcesPage() {
  const { hasData, isLoading, error, refetch } = useIssueSourceSettings();

  if (isLoading && !hasData) {
    return (
      <Box direction="column" fill="flex">
        <PageHeader centered>
          <PageHeaderTitle>Sources</PageHeaderTitle>
          <PageHeaderSubtitle>
            Define the Sources Members pick to record where a Project came from.
          </PageHeaderSubtitle>
        </PageHeader>
        {/* Mirrors the settled run: the switch row's card and the pool card
          * with its count/search header over the rows — neither card wears
          * a section label, the page title is the label. */}
        <SettingsPageSkeleton
          gap="lg"
          groups={[
            { rows: 1, unlabeled: true },
            { rows: 3, unlabeled: true, header: true },
          ]}
        />
      </Box>
    );
  }

  if (error && !hasData) {
    return (
      <LoadErrorState
        noun="sources"
        error={error}
        onRetry={() => {
          void refetch();
        }}
      />
    );
  }

  return (
    <Box direction="column" fill="flex">
      <ScrollArea>
        <PageHeader centered>
          <PageHeaderTitle>Sources</PageHeaderTitle>
          <PageHeaderSubtitle>
            Define the Sources Members pick to record where a Project came from.
          </PageHeaderSubtitle>
        </PageHeader>
        <Box align="center" paddingY="md" paddingX="md">
          <Box gap="lg" fill="width" maxWidth={SETTINGS_CONTENT_MAX_WIDTH}>
            <SourcesList />

            <SettingsPageGutter />
          </Box>
        </Box>
      </ScrollArea>
    </Box>
  );
}

export const Route = createFileRoute("/(app)/$workspace/settings/issue-sources")({
  // Read-only vocabulary page for internal viewers without the manage key —
  // the verdict inside the page decides create/rename/delete; only guests
  // are denied.
  component: withInternal(IssueSourcesPage),
});
